import { Form } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  useGetProductCategoriesQuery,
  useGetProductCategoryByIdQuery,
  useUpdateProductCategoryMutation,
} from '@app/api/Products';
import type { ModelProductCategory } from '@app/api/Products';
import { PRODUCT_CATEGORIES_PATH } from '@app/constants';
import { useNewUserAccessRights, useUserPrint } from '@app/hooks';
import type {
  AddEditProductCategoryForm,
  AddEditProductCategoryProps,
} from '@app/pages/Warehouses/ProductCategories/components/AddEditProductCategory/types';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { useErrorHandler } from '@app/shared/hooks';

import type { DefaultOptionType } from 'antd/es/select';

const { useForm } = Form;

export const useAddEditProductCategory = ({
  mode,
}: AddEditProductCategoryProps) => {
  const [searchParams] = useSearchParams();
  const { activeBranchId: ActiveBranchId } = useUserPrint();
  const { showNotification } = useNotification();
  const categoryId = useMemo(() => {
    if (mode !== 'edit') {
      return null;
    }

    const id = searchParams.get('id');

    if (id) {
      const parsedId = parseInt(id, 10);
      if (isNaN(parsedId)) {
        showNotification(
          'Некорректный идентификатор  категории продукта. Пожалуйста, проверьте URL.',
          'frontWarning'
        );
        return null;
      }
      return parsedId;
    }
    return null;
  }, [searchParams, mode, showNotification]);

  const [updateProductCategory, { isLoading: isUpdateProductCategoryLoading }] =
    useUpdateProductCategoryMutation();
  const navigate = useNavigate();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [form] = useForm<AddEditProductCategoryForm>();
  const { resetFields, setFieldsValue } = form;

  const { data, isFetching, isLoading, isUninitialized, isError, error } =
    useGetProductCategoryByIdQuery(
      {
        ActiveBranchId,
        categoryId: categoryId || 0,
      },
      { skip: !categoryId || mode !== 'edit' }
    );

  const {
    data: productCategoriesData,
    isFetching: isProductCategoriesFetching,
    isLoading: isProductCategoriesLoading,
    isUninitialized: isProductCategoriesUninitialized,
    isError: isProductCategoriesError,
    error: productCategoriesError,
  } = useGetProductCategoriesQuery({
    ActiveBranchId,
  });

  useErrorHandler([
    { flag: isError, error },
    {
      flag: isProductCategoriesError,
      error: productCategoriesError,
    },
  ]);

  const {
    userAccessRights: {
      Products: {
        IsProductCategoryCreate,
        IsProductCategoryDelete,
        IsProductCategoryEdit,
      },
    },
  } = useNewUserAccessRights();

  const isPageForbidden = useMemo(() => {
    switch (mode) {
      case 'add':
        return !IsProductCategoryCreate;
      case 'edit':
        return !IsProductCategoryEdit;
      default:
        return false;
    }
  }, [mode, IsProductCategoryCreate, IsProductCategoryEdit]);

  const categoriesOptions: DefaultOptionType[] = useMemo(() => {
    if (!productCategoriesData) {
      return [];
    }

    return productCategoriesData
      .filter(({ Id }) => Id !== categoryId)
      .map(({ Name, Id }) => ({
        label: Name,
        value: Id,
      }));
  }, [productCategoriesData, categoryId]);

  const disabledForm = useMemo(() => {
    switch (mode) {
      case 'add':
        return (
          isUpdateProductCategoryLoading ||
          isProductCategoriesFetching ||
          isProductCategoriesLoading ||
          isProductCategoriesUninitialized
        );
      case 'edit':
        return (
          isFetching ||
          isLoading ||
          isUninitialized ||
          isUpdateProductCategoryLoading ||
          isProductCategoriesFetching ||
          isProductCategoriesLoading ||
          isProductCategoriesUninitialized
        );
      default:
        return false;
    }
  }, [
    isLoading,
    isFetching,
    isUninitialized,
    isUpdateProductCategoryLoading,
    isProductCategoriesFetching,
    isProductCategoriesLoading,
    isProductCategoriesUninitialized,
    mode,
  ]);

  const skipForm = useCallback(() => {
    resetFields();
  }, [resetFields]);

  const initForm = useCallback(() => {
    if (mode === 'add') {
      skipForm();
      return;
    }
    if (data) {
      const { Name, Comment, ParentCategoryId } = data;
      setFieldsValue({
        Name: Name,
        Comment: Comment || '',
        ParentCategoryId: ParentCategoryId || null,
      });
    }
  }, [skipForm, setFieldsValue, mode, data]);

  const goToProductCategories = useCallback(() => {
    navigate(PRODUCT_CATEGORIES_PATH);
  }, [navigate]);

  const openDeleteModal = useCallback(() => {
    setIsDeleteOpen(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setIsDeleteOpen(false);
  }, []);

  const onCancel = useCallback(() => {
    skipForm();
    closeDeleteModal();
    goToProductCategories();
  }, [skipForm, closeDeleteModal, goToProductCategories]);

  const updateProductCategoryHandler = useCallback(
    async (values: ModelProductCategory) => {
      try {
        const response = await updateProductCategory({
          ActiveBranchId,
          data: values,
        }).unwrap();

        onCancel();

        return Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    [updateProductCategory, onCancel, ActiveBranchId]
  );

  const onFinish = useCallback(
    async ({ Name, Comment, ParentCategoryId }: AddEditProductCategoryForm) => {
      try {
        await updateProductCategoryHandler({
          Id: categoryId || 0,
          Name,
          Comment: Comment || '',
          IsDeleted: false,
          ParentCategoryId: ParentCategoryId || null,
        });

        showNotification(
          `Категория продукта "${Name}" успешно ${
            mode === 'add' ? 'создан' : 'обновлен'
          }.`,
          'success'
        );
      } catch (error) {
        showNotification(error, 'warning');
      }
    },
    [showNotification, categoryId, mode, updateProductCategoryHandler]
  );

  const onDelete = useCallback(async () => {
    if (!data) {
      showNotification('Бренд не найден.', 'frontWarning');
      return;
    }

    try {
      const response = await updateProductCategoryHandler({
        ...data,
        IsDeleted: true,
      });

      const { Name } = response;

      showNotification(
        `Категория продукта "${Name}" успешно удалена.`,
        'success'
      );
      setIsDeleteOpen(false);
      skipForm();
      goToProductCategories();
    } catch (error) {
      showNotification(error, 'warning');
    }
  }, [
    data,
    updateProductCategoryHandler,
    showNotification,
    skipForm,
    goToProductCategories,
  ]);

  return {
    form,
    initForm,
    onFinish,
    openDeleteModal,
    closeDeleteModal,
    onDelete,
    isDeleteOpen,
    onCancel,
    disabledForm,
    skipForm,
    categoriesOptions,
    isPageForbidden,
    IsProductCategoryDelete,
  };
};
